/**
 * A user's direct campaign links on the user form. A campaign can be linked more than once with
 * different link types; one picked in the form (or bulk-added) gets a blank link type.
 */
export type CampaignLink = {
    campaign_id: number;
    link_type: string | null;
};

/** The campaign id behind an autocomplete option (an option object, a row, or a bare id). */
export const campaignIdFromOption = (opt: any): number =>
    Number(opt?.value ?? opt?.id ?? opt?.campaign_id ?? opt);

const hasBlankLink = (links: CampaignLink[], id: number) =>
    links.some(cu => cu.campaign_id === id && (cu.link_type ?? '') === '');

/** Adds a blank-type link to each id that doesn't already have one; skips non-numeric ids. */
export const addBlankCampaignLinks = (links: CampaignLink[], ids: number[]): CampaignLink[] =>
    ids.reduce((next, id) => {
        if (!Number.isFinite(id) || hasBlankLink(next, id)) return next;
        return [...next, { campaign_id: id, link_type: '' }];
    }, links);

/**
 * The ids pasted into "Bulk add campaigns": separated by spaces, new lines, commas or
 * semicolons; anything that isn't a number is dropped and repeats are removed.
 */
export const parseCampaignIdList = (text: string): number[] =>
    Array.from(
        new Set(
            text
                .split(/[\s,;]+/)
                .map(v => v.trim())
                .filter(Boolean)
                .map(v => Number(v))
                .filter(n => Number.isFinite(n)),
        ),
    );
